/** A retirement URL with exactly one preservation boundary, optionally wrapped by ImageKit. */
export interface ImageValidationTarget {
    readonly sourceURL: string | undefined;
    readonly sourceKey: string | undefined;
    readonly token: string | null;
    readonly authorize: (token: string) => string;
    readonly forFreeApp: (appID: string) => string | undefined;
}

export function parseImageValidationTarget(
    target: string,
    endpoint: string,
    depth: number = 0
): ImageValidationTarget | undefined {
    if (depth > 8 || target.length > 65_536) return undefined;
    try {
        const url = new URL(target);
        const origin = new URL(endpoint);
        if (url.username !== "" || url.password !== "" || url.hash !== "") return undefined;
        if (url.origin === origin.origin && url.pathname === origin.pathname) {
            const sourceURL = url.searchParams.get("url") ?? undefined;
            const sourceKey = url.searchParams.get("key") ?? undefined;
            if ((sourceURL === undefined) === (sourceKey === undefined)) return undefined;
            if (sourceKey !== undefined && !/^[a-f0-9]{64}$/.test(sourceKey)) return undefined;
            if (sourceURL !== undefined) {
                const source = new URL(sourceURL);
                if (
                    sourceURL.length > 8192 ||
                    !["http:", "https:"].includes(source.protocol) ||
                    source.username !== "" ||
                    source.password !== ""
                )
                    return undefined;
            }
            let valid = true;
            url.searchParams.forEach((_value, key) => {
                if (
                    !["url", "key", "preview", "fallback", "download", "validation"].includes(key) ||
                    url.searchParams.getAll(key).length !== 1
                )
                    valid = false;
            });
            if (!valid) return undefined;
            return {
                sourceURL,
                sourceKey,
                token: url.searchParams.get("validation"),
                forFreeApp: appID => {
                    if (
                        sourceURL === undefined ||
                        url.searchParams.has("validation") ||
                        !/^[a-zA-Z0-9_-]{1,256}$/.test(appID)
                    )
                        return undefined;
                    const scoped = new URL(url.href);
                    scoped.searchParams.set("rolloutApp", appID);
                    return scoped.href;
                },
                authorize: token => {
                    const authorized = new URL(url.href);
                    authorized.searchParams.set("validation", token);
                    return authorized.href;
                },
            };
        }
        // Keep the exact generated transformation prefix; only replace its encoded source.
        const match = /^(https:\/\/ik\.imagekit\.io\/glideapps\/fetch\/(?:tr:[^/]+\/)?)([^/?#]+)$/.exec(target);
        if (match === null) return undefined;
        const inner = parseImageValidationTarget(decodeURIComponent(match[2]), endpoint, depth + 1);
        if (inner === undefined) return undefined;
        return {
            ...inner,
            authorize: token => match[1] + encodeURIComponent(inner.authorize(token)),
            forFreeApp: appID => {
                const scoped = inner.forFreeApp(appID);
                return scoped === undefined ? undefined : match[1] + encodeURIComponent(scoped);
            },
        };
    } catch (e: unknown) {
        return undefined;
    }
}
